// Admin › Changelog › Platform: the platform's deploys, drawn like the Client
// tab's OTAs. A day stands in for an OTA (the platform deploys on every
// merge), each deployed part for an app, and "Not live yet" for "Not sent yet".

import { useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { cx } from '../../../../components/admin/console';
import { PartIcon } from './PartIcon';
import { KbChanges, type KbGroup } from './KbChanges';
import { AuthorMark, LiveTime, BUSY_NAME_ROOM, BUSY_TEXT_ROOM, BusyMark, Chevron, CommitChip, CopyButton, CopyLinkButton, ExternalLink, KIND_STYLE, KindPills, Notice, PrChip, SectionLabel } from './parts';
import type { CopyFormat } from './UpdateCard';
import { ago, appNames, nzShortDay, nzTime, type SectionKind } from '../data/changelog';
import {
  changeKindCounts,
  dayText,
  partText,
  type DayPart,
  type PlatformChange,
  type PlatformDay,
  type PlatformLivePart,
  type PlatformPending,
} from '../data/platform';

const SECTION_PREVIEW = 6;
const KIND_TITLE: Record<SectionKind, string> = { features: 'Features', fixes: 'Fixes', speedups: 'Speed-ups', reverts: 'Reverts', breaking: 'Breaking changes', other: 'Other' };
const KIND_ORDER: SectionKind[] = ['breaking', 'features', 'fixes', 'speedups', 'reverts', 'other'];

// ---- Live now ----

export function LiveParts({ parts, chosen, onPick, now, busiest = null }: {
  parts: PlatformLivePart[];
  chosen: Set<string>;
  onPick: (part: string) => void;
  now: number;
  /** The part with the most commits lately: its tile gets a small flame. */
  busiest?: string | null;
}) {
  if (!parts.length) return null;
  return (
    <section aria-labelledby="live-parts-h" className="rounded-2xl border border-slate-700 bg-gradient-to-b from-slate-800/55 to-slate-800/30 px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="live-parts-h" className="flex items-center gap-2 text-base font-semibold text-white">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.15)]" aria-hidden="true" />
          Live now
        </h2>
        <span className="text-sm text-slate-400">
          <span className="sm:hidden">Choose one or more parts to filter</span>
          <span className="hidden sm:inline">Choose one or more parts to see only their changes</span>
        </span>
      </div>
      <ul className="mt-3.5 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5 lg:grid-cols-4">
        {parts.map((p) => {
          const selected = chosen.has(p.id);
          return (
            <li key={p.id} className="min-w-0">
              <button
                type="button"
                onClick={() => onPick(p.id)}
                aria-pressed={selected}
                className={cx(
                  'relative flex h-full w-full min-w-0 flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 sm:flex-row sm:items-center sm:gap-3',
                  selected ? 'border-cyan-400/60 bg-cyan-400/[0.08]' : 'border-slate-700 bg-slate-950/45 hover:border-slate-600 hover:bg-slate-800/90',
                )}
              >
                <span className="sm:hidden"><PartIcon part={p.id} size={28} /></span>
                <span className="hidden sm:inline-flex"><PartIcon part={p.id} size={40} /></span>
                <span className={cx('flex w-full min-w-0 flex-col', busiest === p.id && BUSY_TEXT_ROOM)}>
                  <span className={cx('truncate text-[15px] font-semibold text-white', busiest === p.id && BUSY_NAME_ROOM)}>{p.name}</span>
                  <span className="mt-0.5 font-mono text-sm text-slate-200">{p.sha ?? '–'}</span>
                  <span className="text-[13px] leading-snug text-slate-400">{p.deployedAt ? `Deployed ${ago(p.deployedAt, now)}` : 'No deploy since the list began'}</span>
                  {p.latest?.state === 'running' && <span className="mt-0.5 text-[13px] font-medium leading-snug text-cyan-300">Deploying now</span>}
                  {p.latest?.state === 'failed' && <span className="mt-0.5 text-[13px] font-medium leading-snug text-red-300">Last deploy failed</span>}
                </span>
                {busiest === p.id && <BusyMark />}
                {selected && (
                  <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400 text-slate-950" aria-hidden="true">
                    <CheckIcon className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---- one day ----

/**
 * The parts customers use, first to last: a change that went live in several
 * (Router's emails come from the API) is listed once, under the first.
 */
const CUSTOMER_FACING_ORDER = ['router', 'track', 'passagewiki', 'portal', 'api', 'cdn', 'weather', 'routing'];

/** Knowledge base changes, part by part, each change once. */
export function partKbGroups(parts: { id: string; name: string; changes: PlatformChange[] }[]): KbGroup[] {
  const rank = (id: string) => (CUSTOMER_FACING_ORDER.indexOf(id) + 1 || 99);
  const seen = new Set<string>();
  const groups: KbGroup[] = [];
  for (const p of [...parts].sort((a, b) => rank(a.id) - rank(b.id))) {
    const items = p.changes.filter((c) => c.docs?.length && !seen.has(c.sha)).map((c) => ({ key: c.sha, text: c.title, kind: c.kind, docs: c.docs!, sha: c.sha, url: c.url, author: c.author ?? null, pr: c.pr }));
    items.forEach((it) => seen.add(it.key));
    groups.push({ id: p.id, name: p.name, icon: <PartIcon part={p.id} size={24} />, items });
  }
  return groups;
}

/** "API, Portal and Track", or "API, Portal, Track and 4 more": the chips below name them all. */
function dayTitle(parts: { name: string }[]): string {
  return parts.length <= 3 ? appNames(parts) : `${parts.slice(0, 3).map((p) => p.name).join(', ')} and ${parts.length - 3} more`;
}

/** "API 6": a part's tile, name and how many changes it took live that day. */
function PartChip({ part, live }: { part: DayPart; live: boolean }) {
  return (
    <span style={{ padding: 3, paddingRight: 12, borderRadius: 11 }} className="inline-flex items-center gap-2 whitespace-nowrap border border-slate-700 bg-slate-950/50 text-sm text-slate-200">
      <PartIcon part={part.id} size={26} />
      {part.name}
      <span className="text-[13px] tabular-nums text-slate-400">{part.changes.length}</span>
      {live && <span role="img" aria-label="Live now" title="Live now" className="h-2 w-2 rounded-full bg-emerald-400" />}
    </span>
  );
}

export function DayCard({ day, parts, latest, open, isPartOpen, onToggle, onTogglePart, linkTo, format, now, liveSha }: {
  day: PlatformDay;
  /** The parts to show: all of them, or only the chosen ones. */
  parts: DayPart[];
  latest: boolean;
  open: boolean;
  isPartOpen: (part: string) => boolean;
  onToggle: () => void;
  onTogglePart: (part: string) => void;
  linkTo: () => string;
  format: CopyFormat;
  now: number;
  /** Part → the commit live now, to mark the deploy that is still live. */
  liveSha: Record<string, string | null>;
}) {
  const counts = changeKindCounts(parts.flatMap((p) => p.changes));
  const shown: PlatformDay = { ...day, parts };
  const deploys = parts.flatMap((p) => p.deploys);
  const times = deploys.map((d) => d.at).sort();
  const [dayWord, dayNum] = nzShortDay(day.last).split(' ');
  const headId = `day-${day.id}-title`;
  const heading = nzShortDay(day.last);
  return (
    <div id={`day-${day.id}`} className="scroll-mt-20 sm:grid sm:grid-cols-[76px_minmax(0,1fr)] sm:gap-4">
      <div className="relative hidden sm:block" aria-hidden="true">
        <div className="absolute bottom-[-12px] right-[4px] top-0 w-0.5 bg-slate-800" />
        <div className={cx('absolute right-0 top-[18px] h-2.5 w-2.5 rounded-full ring-[3px] ring-slate-900', latest ? 'bg-cyan-400' : 'bg-slate-600')} />
        <div className="pr-[22px] pt-3 text-right">
          <div className="text-[26px] font-bold leading-none text-white">{dayNum}</div>
          <div className="mt-1 text-[13px] text-slate-400">{dayWord}</div>
        </div>
      </div>

      <article aria-labelledby={headId} className={cx('min-w-0 rounded-2xl border bg-slate-800/50', latest ? 'border-cyan-400/35 shadow-[0_8px_30px_-12px_rgba(34,211,238,0.28)]' : 'border-slate-700')}>
        <div className="relative rounded-2xl py-3 pl-2 pr-2 transition-colors hover:bg-slate-700/20 sm:py-3.5 sm:pl-3 sm:pr-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={`day-${day.id}-body`}
              className="flex min-w-0 flex-1 items-start gap-2.5 text-left after:absolute after:inset-0 after:rounded-2xl focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-cyan-500 sm:gap-3"
            >
              <Chevron open={open} className="mt-0.5 h-5 w-5" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <span id={headId} className="text-base font-semibold text-white">
                    <span className="sm:sr-only">{heading}</span>
                    <span className="hidden sm:inline">{dayTitle(parts)}</span>
                  </span>
                  {latest && <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-0.5 text-[13px] font-semibold leading-5 text-cyan-300">Latest</span>}
                </span>
                <span className="text-sm text-slate-400">
                  <span className="whitespace-nowrap">{times.length > 1 ? `${times.length} deploys` : 'Deployed'}</span>{' · '}
                  <span className="whitespace-nowrap">{times.length > 1 ? `${nzTime(times[0])} to ${nzTime(times[times.length - 1])}` : nzTime(times[0])}</span>{' · '}
                  <span className="whitespace-nowrap">{ago(times[times.length - 1], now)}</span>
                </span>
              </span>
            </button>
            <span className="relative z-10 flex flex-none items-center gap-1 sm:gap-1.5">
              <KindPills counts={counts} className="mr-1.5 hidden flex-nowrap lg:flex" />
              <CopyLinkButton getUrl={linkTo} ariaLabel={`Copy a link to ${heading}`} />
              <CopyButton getText={() => dayText(shown, `Platform changes, ${heading}`, format === 'markdown')} ariaLabel={`Copy ${heading}'s changes`} />
            </span>
          </div>
          <div className="mt-2.5 flex flex-col gap-2.5 pl-[30px] sm:pl-8">
            <ul className="flex flex-wrap gap-1.5" aria-label="Parts deployed this day">
              {parts.map((p) => (
                <li key={p.id}>
                  <PartChip part={p} live={!!liveSha[p.id] && p.deploys.some((d) => d.sha === liveSha[p.id])} />
                </li>
              ))}
            </ul>
            <KindPills counts={counts} className="lg:hidden" />
          </div>
        </div>

        {open && (
          <div id={`day-${day.id}-body`} className="space-y-2.5 border-t border-slate-700/60 p-2 sm:p-3">
            {/* The register covers the Scout only so far: the panel shows once a platform change names a row. */}
            <KbChanges groups={partKbGroups(parts)} id={`day-${day.id}-kb`} heading={`Knowledge base changes, ${heading}`} format={format} quietWhenEmpty={false} />
            {parts.map((p) => (
              <PartPanel key={p.id} part={p} open={isPartOpen(p.id)} onToggle={() => onTogglePart(p.id)} format={format} panelId={`day-${day.id}-${p.id}`} />
            ))}
          </div>
        )}
      </article>
    </div>
  );
}

// ---- one part ----

const PENDING_STYLE = {
  running: { label: 'Deploying now', className: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-300' },
  failed: { label: 'Deploy failed', className: 'border-red-400/30 bg-red-400/10 text-red-300' },
  waiting: { label: 'Waiting to deploy', className: 'border-slate-500/30 bg-slate-500/10 text-slate-300' },
} as const;

export function PartPanel({ part: p, open, onToggle, format, panelId, pending }: {
  part: DayPart;
  open: boolean;
  onToggle: () => void;
  format: CopyFormat;
  panelId: string;
  /** Set for a part with changes not live yet: why, and its run. */
  pending?: Pick<PlatformPending, 'state' | 'url'>;
}) {
  const counts = changeKindCounts(p.changes);
  const several = p.deploys.length > 1;
  const status = pending && PENDING_STYLE[pending.state];
  return (
    <section className="rounded-xl border border-slate-700/65 bg-slate-950/35" aria-label={p.name}>
      <div className="flex items-center gap-1 rounded-xl px-1.5 py-1.5 transition-colors hover:bg-slate-700/25 sm:gap-2 sm:px-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`${panelId}-body`}
          className="flex min-h-[48px] min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <Chevron open={open} className="h-[18px] w-[18px]" />
          <PartIcon part={p.id} size={36} />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-base font-semibold text-white">{p.name}</span>
            {status ? (
              <span className={cx('rounded-full border px-2.5 py-0.5 text-[13px] font-semibold', status.className)}>{status.label}</span>
            ) : (
              <span className="whitespace-nowrap rounded-md border border-slate-700 bg-slate-900 px-2 py-0.5 text-[13px] text-slate-400">
                {several ? <>{p.deploys.length} deploys<span className="hidden sm:inline"> · <span className="text-white">{nzTime(p.deploys[p.deploys.length - 1].at)}</span> to <span className="text-white">{nzTime(p.deploys[0].at)}</span></span></> : <span className="text-white">{nzTime(p.deploys[0].at)}</span>}
              </span>
            )}
            <KindPills counts={counts} icons={false} className="hidden md:flex" />
            {counts.length > 0 && <span className="basis-full text-sm text-slate-400 md:hidden">{counts.map((k) => k.label).join(' · ')}</span>}
          </span>
        </button>
        <CopyButton iconOnly getText={() => partText(p, format === 'markdown')} ariaLabel={`Copy ${p.name} changes`} />
      </div>

      {open && (
        <div id={`${panelId}-body`} className="px-3 pb-5 pt-1.5 sm:pl-[60px] sm:pr-5">
          <SectionLabel>What changed</SectionLabel>
          {KIND_ORDER.map((k) => {
            const items = p.changes.filter((c) => c.kind === k);
            return items.length ? <Section key={k} kind={k} items={items} showTimes={several} /> : null;
          })}
          {(pending?.url || p.deploys[0]?.url) && (
            <div className="mt-3">
              <ExternalLink href={(pending?.url ?? p.deploys[0].url)!}>{pending ? 'Deploy run' : several ? 'Latest deploy log' : 'Deploy log'}</ExternalLink>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Section({ kind, items, showTimes }: { kind: SectionKind; items: PlatformChange[]; showTimes: boolean }) {
  // Docs, tests and CI changes stay folded: they rarely change what anyone sees.
  const quiet = kind === 'other';
  const [all, setAll] = useState(false);
  const style = KIND_STYLE[kind];
  const limit = quiet ? 0 : SECTION_PREVIEW;
  const shown = all ? items : items.slice(0, limit);
  const hidden = items.length - shown.length;
  return (
    <div className="mt-3.5">
      {quiet ? (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          aria-expanded={all}
          className="-ml-1 inline-flex min-h-[36px] items-center gap-2 rounded-md px-1 text-sm font-semibold text-slate-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <Chevron open={all} className="h-4 w-4" />
          Docs, tests and other <span className="font-medium text-slate-500">{items.length}</span>
        </button>
      ) : (
        <div className={cx('flex items-center gap-2 text-sm font-semibold', style.head)}>
          <style.Icon className="h-4 w-4" aria-hidden="true" />
          {KIND_TITLE[kind]} <span className="font-medium text-slate-500">{items.length}</span>
        </div>
      )}
      {shown.length > 0 && (
        <ul className="mt-1.5">
          {shown.map((c) => (
            <li key={c.sha} className="flex items-baseline gap-3 py-[5px] text-[15px] leading-snug text-slate-300">
              <span className={cx('h-1.5 w-1.5 flex-none -translate-y-0.5 rounded-full', style.dot)} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                {c.title}
                {c.summary && <span title={c.summary} className="mt-0.5 line-clamp-2 text-sm leading-relaxed text-slate-500 sm:line-clamp-none">{c.summary}</span>}
              </span>
              <span className="flex flex-none items-center gap-1.5 self-start">
                {c.author && <AuthorMark author={c.author} />}
                {showTimes && c.deployedAt && <span className="hidden sm:inline"><LiveTime at={c.deployedAt} /></span>}
                {c.pr && <PrChip number={c.pr.number} url={c.pr.url} />}
                <CommitChip sha={c.sha} url={c.url} />
              </span>
            </li>
          ))}
        </ul>
      )}
      {!quiet && (hidden > 0 || (all && items.length > limit)) && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-1.5 inline-flex min-h-[36px] items-center rounded-lg border border-slate-700 px-3 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          {all ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

// ---- not live yet ----

export function PlatformPendingList({ pending, isOpen, onToggle, format, filtered }: {
  pending: PlatformPending[];
  isOpen: (part: string) => boolean;
  onToggle: (part: string) => void;
  format: CopyFormat;
  filtered: number;
}) {
  if (!pending.length) {
    return (
      <p className="text-[15px] text-slate-400">
        {filtered ? `Everything for ${filtered === 1 ? 'this part' : 'these parts'} is live.` : 'Everything on main is live.'}
      </p>
    );
  }
  const failed = pending.some((p) => p.state === 'failed');
  return (
    <section aria-label="Not live yet" className="space-y-3">
      {failed && <Notice tone="error">A deploy failed. Its changes go live with the next deploy that works.</Notice>}
      <div className="space-y-2.5">
        <KbChanges groups={partKbGroups(pending.map((p) => ({ id: p.part, name: p.name, changes: p.changes })))} id="pending-kb" heading="Knowledge base changes, not live yet" format={format} quietWhenEmpty={false} />
        {pending.map((p) => (
          <div key={p.part} id={`pending-${p.part}`} className="scroll-mt-20">
            <PartPanel
              part={{ id: p.part, name: p.name, deploys: [], changes: p.changes }}
              open={isOpen(p.part)}
              onToggle={() => onToggle(p.part)}
              format={format}
              panelId={`pending-${p.part}`}
              pending={{ state: p.state, url: p.url }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
